import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { STORAGE_KEYS } from '../constants';
import { AdminAccount } from '../types';
import { Permission, hasPageAccess, resolveUserPermissions } from '../config/permissions';
import { apiClient } from '../services/api';
import { queryClient } from '../queryClient';

interface AuthState {
  user: AdminAccount | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthContextValue extends AuthState {
  login: (user: AdminAccount, token: string) => void;
  logout: () => void;
  updateUser: (updatedUser: AdminAccount) => void;
  hasPermission: (permission?: Permission) => boolean;
  hasRole: (role?: string | string[]) => boolean;
  getResolvedPermissions: () => Permission[];
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: true,
  });

  const logout = useCallback(() => {
    // Prevent cached data from leaking across accounts within the same session.
    try {
      queryClient.cancelQueries();
    } catch {
      // no-op
    }
    queryClient.clear();

    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);

    setAuthState({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
    });
  }, []);

  // Initialize auth state from localStorage
  useEffect(() => {
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);

    console.log('=== INITIALIZING AUTH STATE ===');
    console.log('Token exists:', !!token);
    console.log('User data exists:', !!userData);
    console.log('User data:', userData);

    if (token && userData) {
      try {
        const user = JSON.parse(userData);
        console.log('Parsed user:', user);
        console.log('User role:', user.role);
        setAuthState({
          user,
          token,
          isAuthenticated: true,
          isLoading: true, // Set loading to true while fetching permissions
        });
      } catch (error) {
        console.error('Error parsing user data:', error);
        logout();
      }
    } else {
      setAuthState(prev => ({ ...prev, isLoading: false }));
    }
  }, [logout]);

  // Fetch permissions from API when authenticated
  useEffect(() => {
    const fetchPermissions = async () => {
      if (!authState.isAuthenticated || !authState.token || !authState.user) {
        return;
      }

      // If already have permissionObjects, skip fetch
      if (authState.user.permissionObjects && authState.user.permissionObjects.length > 0) {
        setAuthState(prev => ({ ...prev, isLoading: false }));
        return;
      }

      try {
        console.log('=== FETCHING PERMISSIONS FROM API ===');
        const response = await apiClient.getCurrentAdmin();
        console.log('=== PERMISSIONS FETCHED ===');
        console.log('Response:', response);

        if (response.data?.admin && response.data?.permissions) {
          const updatedUser: AdminAccount = {
            ...response.data.admin,
            permissionObjects: response.data.permissions,
            permissions: response.data.permissions.map((p: any) => p.code),
          };

          localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(updatedUser));
          setAuthState(prev => ({
            ...prev,
            user: updatedUser,
            isLoading: false,
          }));
        } else {
          setAuthState(prev => ({ ...prev, isLoading: false }));
        }
      } catch (error) {
        console.error('Error fetching permissions:', error);
        // If fetch fails, still allow access with existing user data
        setAuthState(prev => ({ ...prev, isLoading: false }));
      }
    };

    fetchPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState.isAuthenticated, authState.token]);

  const login = useCallback((user: AdminAccount, token: string) => {
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
    localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));

    setAuthState({
      user,
      token,
      isAuthenticated: true,
      isLoading: false,
    });
  }, []);

  const updateUser = useCallback((updatedUser: AdminAccount) => {
    localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(updatedUser));
    setAuthState(prev => ({
      ...prev,
      user: updatedUser,
    }));
  }, []);

  const hasPermission = useCallback(
    (permission?: Permission): boolean => {
      return hasPageAccess(authState.user, permission);
    },
    [authState.user],
  );

  const hasRole = useCallback(
    (role?: string | string[]): boolean => {
      if (!role) return true;
      if (!authState.user) return false;
      if (Array.isArray(role)) {
        return role.includes(authState.user.role || '');
      }
      return authState.user.role === role;
    },
    [authState.user],
  );

  const value: AuthContextValue = {
    ...authState,
    login,
    logout,
    updateUser,
    hasPermission,
    hasRole,
    getResolvedPermissions: () => resolveUserPermissions(authState.user),
  };

  useEffect(() => {
    const handleUnauthorized = () => {
      logout();
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
    };
  }, [logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
};

// Hook to check permission for a component
export const usePermission = (permission?: Permission) => {
  const { hasPermission } = useAuth();
  return hasPermission(permission);
};

// Hook to check role for a component
export const useRole = (role: string | string[]) => {
  const { hasRole } = useAuth();
  return hasRole(role);
};


